/** Parsed day limits only (no defaults). Use for validation and “blank until user picks”. */
export function parseIntervalDaysStrict(raw) {
  const ints = (raw || [])
    .map((x) => parseInt(String(x).trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0);
  return [...new Set(ints)].sort((a, b) => a - b);
}

/** Upper bounds for API: positive, sorted, unique; falls back when nothing valid is set. */
export function normalizeIntervalDays(raw) {
  const ints = parseIntervalDaysStrict(raw);
  return ints.length ? ints : [30, 60, 90, 120];
}

/** Build query string for GET /inventory/aging/report */
export function buildInventoryAgingQuery(criteria, search, pagination = null) {
  const p = new URLSearchParams();
  p.set('report_date', criteria.reportDate);

  const ints = normalizeIntervalDays(criteria.intervalDays);
  p.set('intervals', ints.join(','));

  if (criteria.codeFrom) p.set('code_from', criteria.codeFrom);
  if (criteria.codeTo) p.set('code_to', criteria.codeTo);
  if (criteria.itemGroupId && criteria.itemGroupId !== 'All') {
    p.set('item_group_id', String(criteria.itemGroupId));
  }
  p.set('properties_mode', criteria.propertiesMode === 'include' ? 'include' : 'ignore');

  if (criteria.whIncludeOn) {
    p.set('wh_include_on', '1');
    if (criteria.whIncludeFrom) p.set('wh_include_from', criteria.whIncludeFrom);
    if (criteria.whIncludeTo) p.set('wh_include_to', criteria.whIncludeTo);
  }
  if (criteria.whExcludeOn) {
    p.set('wh_exclude_on', '1');
    if (criteria.whExcludeFrom) p.set('wh_exclude_from', criteria.whExcludeFrom);
    if (criteria.whExcludeTo) p.set('wh_exclude_to', criteria.whExcludeTo);
  }

  p.set('issue_strategy', criteria.issueStrategy === 'fifo' ? 'fifo' : 'lifo');
  p.set('valuation', criteria.valuation === 'current' ? 'current' : 'document');

  if (search) p.set('search', search);

  if (pagination && pagination.page >= 1 && pagination.itemsPerPage >= 1) {
    p.set('page', String(pagination.page));
    p.set('items_per_page', String(pagination.itemsPerPage));
  }
  return p.toString();
}

/**
 * SAP-style column groups for the aging grid (aligned with server keys: current, b1..bN, over).
 * Example intervals [30]: "0–30 Days" (current+b1), "Older than 30 days" (over).
 * Example [30,60,90]: "0–30", "31–60", "61–90", "Older than 90 days".
 */
export function inventoryAgingGridBands(intervalsRaw) {
  const ints = parseIntervalDaysStrict(intervalsRaw);
  if (!ints.length) return { intervals: [], bands: [] };
  const bands = [];
  bands.push({
    groupLabel: `0–${ints[0]} Days`,
    bucketKeys: ['current', 'b1'],
  });
  for (let k = 1; k < ints.length; k += 1) {
    const lo = ints[k - 1] + 1;
    const hi = ints[k];
    bands.push({
      groupLabel: `${lo}–${hi} Days`,
      bucketKeys: [`b${k + 1}`],
    });
  }
  const last = ints[ints.length - 1];
  bands.push({
    groupLabel: `Older than ${last} days`,
    bucketKeys: ['over'],
  });
  return { intervals: ints, bands };
}

export function sumBucketKeys(buckets, keys) {
  if (!buckets || !keys?.length) return { qty: 0, value: 0 };
  return keys.reduce(
    (acc, key) => {
      const c = buckets[key] || { qty: 0, value: 0 };
      acc.qty += Number(c.qty) || 0;
      acc.value += Number(c.value) || 0;
      return acc;
    },
    { qty: 0, value: 0 }
  );
}

/**
 * Collapse API bucket map to SAP-style two columns after "Total":
 * - Total: full stock
 * - Older than 0 days: ages 1+ (all buckets except total + current)
 */
export function summarizeInventoryAgingDisplay(buckets) {
  if (!buckets) {
    return {
      total: { qty: 0, value: 0 },
      olderThan0: { qty: 0, value: 0 },
    };
  }
  const total = buckets.total || { qty: 0, value: 0 };
  const olderThan0 = { qty: 0, value: 0 };
  Object.entries(buckets).forEach(([key, cell]) => {
    if (key === 'total' || key === 'current') return;
    olderThan0.qty += Number(cell?.qty) || 0;
    olderThan0.value += Number(cell?.value) || 0;
  });
  return { total, olderThan0 };
}

export function defaultInventoryAgingCriteria() {
  return {
    reportDate: new Date().toISOString().slice(0, 10),
    codeFrom: '',
    codeTo: '',
    itemGroupId: 'All',
    propertiesMode: 'ignore',
    whIncludeOn: false,
    whIncludeFrom: '',
    whIncludeTo: '',
    whExcludeOn: false,
    whExcludeFrom: '',
    whExcludeTo: '',
    issueStrategy: 'lifo',
    valuation: 'document',
    intervalDays: [],
  };
}
